import { Animated, Easing, StyleSheet, Text, View } from "react-native";
import { Animations, Colors, Metrics, Typography } from "../../resources";
import {
  Dashboard,
  DataType,
  Headers,
  SIconType,
  Sorting,
  Space,
} from "../../types";
import React, { useEffect, useRef, useState } from "react";
import { calculatePercentage, filterSpaces } from "../../utils";
import { isNumber, stubTrue } from "lodash";
import {
  useApi,
  useQueryParams,
  useReports,
  useSettings,
  useSpaces,
  useTags,
} from "../../hooks";

import Lottie from "lottie-react-web";
import SChartView from "../organisms/SChartView";
import STableView from "../organisms/STableView";
import SToolBar from "../organisms/SToolBar";
import { Toaster } from "@density/ui";
import moment from "moment";
import useDeepCompareEffect from "use-deep-compare-effect";

interface IProps {
  headers: Headers;
}

const SDashboard: React.FC<IProps> = ({ headers }) => {
  const [filteredSpaces, setFilteredSpaces] = useState<Space[]>([]);
  const [skeleton, setSkeleton] = useState<boolean>(false);
  const { spaces } = useSpaces();
  const { reports } = useReports();
  const { settings, writeSetting } = useSettings();
  const {
    capacity,
    safe,
    dashboard,
    date,
    impersonate,
    sort,
    order,
    tags: tagsString,
  } = useQueryParams();

  const { loading, addTag, removeTag, updateCapacity, updateSafeCapacity } =
    useApi(headers, settings?.impersonate);

  const { tags, addTags, setSelected } = useTags();
  const { checkSpace } = useSpaces();

  const opacityRef = useRef<Animated.Value>(new Animated.Value(0));

  const mainDate = settings?.date
    ? moment(settings?.date).format("YYYY-MM-DD")
    : "";

  useEffect(() => {
    if (!loading) {
      Animated.timing(opacityRef.current, {
        toValue: 1,
        duration: 500,
        easing: Easing.sin,
        useNativeDriver: true,
      }).start();
    }
  }, [loading]);

  useEffect(() => {
    if (date) {
      writeSetting({
        date: moment(date).startOf("isoWeek").format("YYYY-MM-DD"),
      });
    }
  }, [date]);

  useEffect(() => {
    if (dashboard) {
      writeSetting({
        dashboard: dashboard === "table" ? Dashboard.Table : Dashboard.Chart,
      });
    }
  }, [dashboard]);

  useEffect(() => {
    if (impersonate) {
      writeSetting({
        impersonate: impersonate.toString(),
      });
    }
  }, [impersonate]);

  useEffect(() => {
    if (sort) {
      writeSetting({
        sort: sort.toString(),
      });
    }
  }, [sort]);

  useEffect(() => {
    if (safe) {
      writeSetting({
        safe:
          safe === DataType.SafeCapacityPercentage
            ? DataType.SafeCapacityPercentage
            : DataType.SafeCapacityPpl,
        busiest:
          safe === DataType.SafeCapacityPercentage
            ? DataType.BusiestPercentage
            : DataType.BusiestPpl,
        average:
          safe === DataType.SafeCapacityPercentage
            ? DataType.AveragePercentage
            : DataType.AveragePpl,
      });
    }
  }, [safe]);

  useEffect(() => {
    if (order) {
      writeSetting({
        order: order.toString(),
      });
    }
  }, [order]);

  useEffect(() => {
    if (capacity) {
      writeSetting({
        capacity: capacity.toString(),
      });
    }
  }, [capacity]);

  const selectTagsFromQuery = () => {
    if (tagsString) {
      const tagsArray = tagsString.toString().split(",");
      Object.keys(tags).map((id: string) => {
        if (tagsArray.includes(id)) {
          setSelected(id);
        }
      });
    }
  };

  useEffect(() => {
    selectTagsFromQuery();
  }, [tagsString]);

  useDeepCompareEffect(() => {
    const processSpaces = async () => {
      return new Promise<Space[]>((resolve, reject) => {
        //    Filter spaces from tags
        for (let id of Object.keys(tags)) {
          if (tags[id].selected) {
            break;
          }
        }

        //Initialize to no filters so all spaces are selected
        let newSpaces = spaces.map((space: Space) => {
          space.filtered = true;
          return space;
        });
        // const selectedTags = Object.keys(tags).filter((id: string) => {
        //   return tags[id].selected;
        // });

        // filter spaces by tags and search term
        let sortedSpaces = filterSpaces(newSpaces, tags, settings?.search);

        // Uncheck all checked spaces that were filtered out
        newSpaces
          .filter((space: Space) => !sortedSpaces.includes(space))
          .map((space: Space) => {
            checkSpace(space.id, false);
          });

        // Sort the spaces
        if (
          settings?.order === Sorting.Incremental ||
          settings?.order === Sorting.Decremental
        ) {
          // Filter the nulls in an array and the rest in another
          let nulls: Space[];
          let toSort: Space[];

          switch (settings?.sort) {
            case DataType.Name:
              nulls = sortedSpaces.filter((space: Space) => !space.name);
              toSort = sortedSpaces.filter((space: Space) => space.name);
              break;
            case DataType.TargetCapacity:
              nulls = sortedSpaces.filter(
                (space: Space) =>
                  !space.target_capacity && space.target_capacity !== 0
              );
              toSort = sortedSpaces.filter(
                (space: Space) =>
                  space.target_capacity || space.target_capacity === 0
              );
              break;
            case DataType.LegalCapacity:
              nulls = sortedSpaces.filter(
                (space: Space) =>
                  !space.legal_capacity &&
                  space.legal_capacity !== 0 &&
                  !space.target_capacity &&
                  space.target_capacity !== 0
              );
              toSort = sortedSpaces.filter(
                (space: Space) =>
                  space.legal_capacity ||
                  space.legal_capacity === 0 ||
                  space.target_capacity ||
                  space.target_capacity === 0
              );
              break;
            case DataType.SafeCapacityPercentage:
              nulls = sortedSpaces.filter(
                (space: Space) =>
                  !space.safe_capacity && space.safe_capacity !== 0
              );
              toSort = sortedSpaces.filter(
                (space: Space) =>
                  space.safe_capacity || space.safe_capacity === 0
              );
              break;
            case DataType.SafeCapacityPpl:
              nulls = sortedSpaces.filter(
                (space: Space) =>
                  !space.safe_capacity && space.safe_capacity !== 0
              );
              toSort = sortedSpaces.filter(
                (space: Space) =>
                  space.safe_capacity || space.safe_capacity === 0
              );
              break;
            default:
              nulls = [];
              toSort = sortedSpaces;
              break;
          }

          sortedSpaces = toSort.sort((a: Space, b: Space) => {
            let aValue: string | number;
            let bValue: string | number;

            const aReport =
              reports && reports[a.id]
                ? reports[a.id]["weekly"][mainDate]
                : null;
            const bReport =
              reports && reports[b.id]
                ? reports[b.id]["weekly"][mainDate]
                : null;

            switch (settings?.sort) {
              case DataType.Name:
                aValue = a.name.toUpperCase();
                bValue = b.name.toUpperCase();
                break;
              case DataType.TargetCapacity:
                aValue =
                  (a.safe_capacity || a.safe_capacity === 0) &&
                  a.target_capacity
                    ? a.target_capacity
                    : -1;
                bValue =
                  (b.safe_capacity || b.safe_capacity === 0) &&
                  b.target_capacity
                    ? b.target_capacity
                    : -1;
                break;
              case DataType.LegalCapacity:
                aValue =
                  !a.legal_capacity && a.legal_capacity !== 0
                    ? a.target_capacity
                    : a.legal_capacity;
                bValue =
                  !b.legal_capacity && b.legal_capacity !== 0
                    ? b.target_capacity
                    : b.legal_capacity;
                break;
              case DataType.SafeCapacityPercentage:
                aValue =
                  a.target_capacity !== null
                    ? calculatePercentage(a.safe_capacity, a.target_capacity)
                    : -1;
                bValue =
                  b.target_capacity !== null
                    ? calculatePercentage(b.safe_capacity, b.target_capacity)
                    : -1;
                break;
              case DataType.SafeCapacityPpl:
                aValue = a.safe_capacity;
                bValue = b.safe_capacity;
                break;
              case DataType.BusiestPercentage:
                aValue =
                  (a.safe_capacity || a.safe_capacity === 0) &&
                  a.target_capacity
                    ? calculatePercentage(
                        aReport?.max ? aReport?.max : 0,
                        a.target_capacity
                      )
                    : -1;
                bValue =
                  (b.safe_capacity || b.safe_capacity === 0) &&
                  b.target_capacity
                    ? calculatePercentage(
                        bReport?.max ? bReport?.max : 0,
                        b.target_capacity
                      )
                    : -1;
                break;
              case DataType.BusiestPpl:
                aValue = aReport?.max ? aReport.max : 0;
                bValue = bReport?.max ? bReport.max : 0;
                break;
              case DataType.AveragePercentage:
                aValue =
                  (a.safe_capacity || a.safe_capacity === 0) &&
                  a.target_capacity
                    ? calculatePercentage(
                        aReport?.average_max ? aReport?.average_max : 0,
                        a.target_capacity
                      )
                    : -1;
                bValue =
                  (b.safe_capacity || b.safe_capacity === 0) &&
                  b.target_capacity
                    ? calculatePercentage(
                        bReport?.average_max ? bReport?.average_max : 0,
                        b.target_capacity
                      )
                    : -1;
                break;
              case DataType.AveragePpl:
                aValue = Math.round(
                  aReport?.average_max ? aReport.average_max : 0
                );
                bValue = Math.round(
                  bReport?.average_max ? bReport.average_max : 0
                );
                break;
              default:
                aValue = a.name.toUpperCase();
                bValue = b.name.toUpperCase();
                break;
            }

            // if value is not a number or empty then send to the bottom of the sorted listRow
            let result = aValue < bValue ? -1 : 1;
            if (settings?.order === Sorting.Incremental) {
              result = aValue > bValue ? -1 : 1;
            }
            return aValue === bValue ? 0 : result;
          });
          sortedSpaces = [...sortedSpaces, ...nulls];
        }
        resolve(sortedSpaces);
      });
    };

    setSkeleton(true);
    processSpaces()
      .then((_spaces: Space[]) => setFilteredSpaces(_spaces))
      .catch((error) => {})
      .finally(() => {
        setSkeleton(false);
      });
  }, [spaces, tags, settings?.order, settings?.sort, settings?.search]);

  useDeepCompareEffect(() => {
    let tagNumbers = tags;
    // reset tag numbers
    for (let id of Object.keys(tags)) {
      //    setNumber(id, 0);
      tagNumbers[id].number = 0;
    }

    // Set tags filters and numbers
    filteredSpaces.map((space: Space) => {
      space.tags.map((id: string) => {
        tagNumbers[id].number++;
      });
    });

    addTags(tagNumbers);
  }, [filteredSpaces]);

  const mainView =
    settings?.dashboard === Dashboard.Table ? (
      <STableView
        spaces={filteredSpaces}
        reports={reports}
        updateCapacity={updateCapacity}
        updateSafeCapacity={updateSafeCapacity}
        addTag={addTag}
        removeTag={removeTag}
      />
    ) : (
      <SChartView
        spaces={filteredSpaces}
        reports={reports}
        updateCapacity={updateCapacity}
        updateSafeCapacity={updateSafeCapacity}
      />
    );

  const opacity = opacityRef.current.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  });

  return (
    <>
      <SToolBar
        view={
          settings?.dashboard === Dashboard.Table
            ? SIconType.Chart
            : SIconType.Table
        }
        updateSafeCapacity={updateSafeCapacity}
        addTag={addTag}
        removeTag={removeTag}
      />
      {/* <SProgress disabled={!bgLoading} /> */}
      {loading ? (
        <View style={styles.spinner}>
          <Lottie
            width={Metrics.size160}
            height={Metrics.size160}
            options={{
              animationData: Animations.spinner,
            }}
          />
          <Text style={styles.loadingText}>Preparing data...</Text>
        </View>
      ) : (
        <Animated.View style={[styles.main, { opacity: opacity }]}>
          {mainView}
        </Animated.View>
      )}
      <Toaster />
    </>
  );
};

const styles = StyleSheet.create({
  main: {
    flex: 1,
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  spinner: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: Metrics.paddingSmallStandard,
    ...Typography.headline2,
  },
});

export default SDashboard;
